import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { COPY, SUPPORTED_LANGUAGES } from "../i18n/translations";

const LanguageContext = createContext(null);

function normalizeBrowserLanguage(input) {
    const v = (input || "").toLowerCase();
    if (v.startsWith("tr")) return "tr";
    if (v.startsWith("ru")) return "ru";
    if (v.startsWith("es")) return "es";
    if (v.startsWith("pt")) return "pt";
    return "en";
}

function detectInitialLanguage() {
    const stored = localStorage.getItem("adrise_lang");
    if (stored && SUPPORTED_LANGUAGES.includes(stored)) return stored;
    const candidates = Array.isArray(navigator.languages) && navigator.languages.length
        ? navigator.languages
        : [navigator.language || "en"];
    for (const c of candidates) {
        const n = normalizeBrowserLanguage(c);
        if (SUPPORTED_LANGUAGES.includes(n)) return n;
    }
    return "en";
}

function lookup(path, lang, fallback = "") {
    const parts = path.split(".");
    let node = COPY;
    for (const p of parts) {
        node = node?.[p];
    }
    if (!node || typeof node !== "object") return fallback;
    return node[lang] || node.en || fallback;
}

export function LanguageProvider({ children }) {
    const [language, setLanguage] = useState(() => detectInitialLanguage());

    useEffect(() => {
        localStorage.setItem("adrise_lang", language);
        document.documentElement.lang = language;
    }, [language]);

    const value = useMemo(
        () => ({
            language,
            setLanguage,
            t: (path, fallback = "") => lookup(path, language, fallback),
        }),
        [language],
    );

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    const ctx = useContext(LanguageContext);
    if (!ctx) {
        throw new Error("useLanguage must be used inside LanguageProvider");
    }
    return ctx;
}
